// src/layouts/AuthLayout.tsx
// Centered layout wrapper for authentication pages (login, oauth callback).
// Features the SGS church branding on the left and the form on the right.

import { Outlet } from 'react-router-dom'
import LogoWhite from '@/assets/logo_white.png'

export function AuthLayout() {
  return (
    <div className="min-h-screen flex bg-background">
      {/* ── Left panel — church branding ─────────────────── */}
      <div
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #1E3A5F 0%, #0D2240 60%, #0A1628 100%)' }}
      >
        {/* Background decorative circles */}
        <div
          className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #D4A843 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #2E7D32 0%, transparent 70%)' }}
        />
        <div
          className="absolute top-1/2 right-8 w-48 h-48 rounded-full opacity-5"
          style={{ background: 'radial-gradient(circle, #D4A843 0%, transparent 70%)' }}
        />

        {/* Logo area */}
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            {/* Church icon SVG */}
            <div
              className="w-18 h-18 flex items-center justify-center"
            >
              <img src={LogoWhite} alt="SGS Logo" className="w-18 h-18 object-contain" />
            </div>
            <div>
              <div className="text-white font-bold text-xl tracking-wide">SGS Connect</div>
              <div className="text-xs tracking-widest uppercase" style={{ color: '#D4A843' }}>
                Admin Dashboard
              </div>
            </div>
          </div>
        </div>

        {/* Center tagline */}
        <div className="relative z-10 max-w-md">
          <blockquote
            className="text-2xl xl:text-3xl font-light leading-relaxed mb-6"
            style={{ color: 'rgba(255,255,255,0.9)', fontFamily: "'Merriweather', Georgia, serif" }}
          >
            "For where two or three gather in my name, there am I with them."
          </blockquote>
          <cite
            className="text-sm tracking-wider uppercase not-italic"
            style={{ color: '#D4A843' }}
          >
            — Matthew 18:20
          </cite>
          <p className="mt-8 text-sm leading-relaxed" style={{ color: 'rgba(168, 191, 218, 0.8)' }}>
            Manage your church community, events, sermons, and more from one
            beautifully designed dashboard.
          </p>
        </div>

        {/* Bottom feature list */}
        <div className="relative z-10">
          <div className="grid grid-cols-3 gap-4">
            {[
              { icon: '👥', label: 'Members' },
              { icon: '📅', label: 'Events' },
              { icon: '🎙️', label: 'Sermons' },
            ].map((f) => (
              <div key={f.label} className="text-center">
                <div className="text-2xl mb-1">{f.icon}</div>
                <div className="text-xs uppercase tracking-wider" style={{ color: 'rgba(168, 191, 218, 0.7)' }}>
                  {f.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel — form content ────────────────────── */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 sm:p-10 lg:p-16 bg-background">
        {/* Mobile logo (only shown on small screens) */}
        <div className="lg:hidden mb-8 flex items-center gap-3">
          <div
            className="w-10 h-10 flex items-center justify-center rounded-lg"
            style={{ background: '#1E3A5F' }}
          >
            <img src={LogoWhite} alt="SGS Logo" className="w-8 h-8 object-contain" />
          </div>
          <div>
            <div className="font-bold text-lg" style={{ color: '#1E3A5F' }}>SGS Connect</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">Admin Dashboard</div>
          </div>
        </div>

        {/* Form outlet */}
        <div className="w-full max-w-md">
          <Outlet />
        </div>

        {/* Footer */}
        <p className="mt-8 text-xs text-muted-foreground text-center">
          © {new Date().getFullYear()} SGS Church. All rights reserved.
        </p>
      </div>
    </div>
  )
}
